import { Action, NgxsOnInit, Selector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { InvitationsService } from '@app/modules/invitations/service/invitations.service';
import {
  IAllInvitation,
  IInvitation,
} from '@app/modules/invitations/interface/invitation';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { InvitationsActions } from '@app/modules/invitations/store/invitations.actions';
import { INVITATION_STATUS } from '../consts/status';
import { hasLiveSession } from '@app/modules/auth/utils/has-live-session';
import { AuthActions } from '@app/modules/auth/store/auth.actions';

interface InvitationsStateModel {
  loading: boolean;
  items: IAllInvitation[];
}

@State<InvitationsStateModel>({
  name: 'invitations',
  defaults: {
    loading: true,
    items: null,
  },
})
@Injectable()
export class InvitationsState implements NgxsOnInit {
  @Selector()
  static loading(state?: InvitationsStateModel): boolean {
    return state?.loading ?? true;
  }

  @Selector()
  static allInvitations(state?: InvitationsStateModel): IAllInvitation[] {
    return state?.items ?? [];
  }

  // items starts null, and the whole slice is undefined until the lazy module
  // registers it (the notifications sidebar composes these selectors earlier).
  // NGXS 18 logs (instead of swallowing) selector throws, so guard both.
  @Selector()
  static newInvitations(state?: InvitationsStateModel): IAllInvitation[] {
    return (state?.items ?? []).filter((x) => x.status === INVITATION_STATUS.pending);
  }

  @Selector()
  static newInvitationsCount(state?: InvitationsStateModel): number {
    return (state?.items ?? []).filter((x) => x.status === INVITATION_STATUS.pending)
      .length;
  }

  @Selector()
  static sentInvitations(state?: InvitationsStateModel): IInvitation[] {
    return (state?.items ?? []).map((x) => x.invaiter);
  }

  @Selector()
  static receivedInvitations(state?: InvitationsStateModel): IInvitation[] {
    return (state?.items ?? []).map((x) => x.recepient);
  }

  /**
   * @param service
   */
  constructor(private readonly service: InvitationsService) {}

  ngxsOnInit(ctx: StateContext<InvitationsStateModel>): void {
    // Signed out (or a public break-glass page): fetch nothing. These
    // requests could only 401, and on the emergency card they compete with
    // the one request that matters.
    if (!hasLiveSession()) {
      // Same rule as the living will: `loading` starts true, so a skipped
      // fetch has to clear it or the invitation form spins forever.
      ctx.patchState({ loading: false });
      return;
    }
    ctx.dispatch([new InvitationsActions.GetAllInvitations()]);
  }

  @Action(
    [
      AuthActions.LoginSuccess,
      InvitationsActions.GetAllInvitations,
      InvitationsActions.AcceptInvitationSuccess,
      InvitationsActions.DeclineInvitationSuccess,
      InvitationsActions.CancelInvitationSuccess,
    ],
    { cancelUncompleted: true }
  )
  getAll(
    ctx: StateContext<InvitationsStateModel>
  ): Observable<void | IAllInvitation[]> {
    ctx.patchState({ loading: true });

    return this.service.getAllInvitations().pipe(
      tap((items) => {
        ctx.patchState({ items });
        ctx.dispatch(new InvitationsActions.GetAllInvitationsSuccess());
      }),
      catchError((e: HttpErrorResponse) =>
        ctx.dispatch(new InvitationsActions.GetAllInvitationsFailed(e))
      )
    );
  }

  @Action(InvitationsActions.SendInvitation, { cancelUncompleted: true })
  send(
    ctx: StateContext<InvitationsStateModel>,
    { model }: InvitationsActions.SendInvitation
  ): Observable<void | IAllInvitation> {
    ctx.patchState({ loading: true });

    return this.service.sendInvitation(model).pipe(
      tap((invitation: IAllInvitation) => {
        const { items } = ctx.getState();
        ctx.patchState({
          items: [...items, invitation],
        });
        ctx.dispatch(new InvitationsActions.SendInvitationSuccess(model));
      }),
      catchError((e: HttpErrorResponse) =>
        ctx.dispatch(new InvitationsActions.SendInvitationFailed(e))
      )
    );
  }

  @Action(InvitationsActions.AcceptInvitation, { cancelUncompleted: true })
  accept(
    ctx: StateContext<InvitationsStateModel>,
    { invitationId }: InvitationsActions.AcceptInvitation
  ): Observable<void | boolean> {
    ctx.patchState({ loading: true });

    return this.service.acceptInvitation(invitationId).pipe(
      tap(() => ctx.dispatch(new InvitationsActions.AcceptInvitationSuccess())),
      catchError((e: HttpErrorResponse) =>
        ctx.dispatch(new InvitationsActions.AcceptInvitationFailed(e))
      )
    );
  }

  @Action(InvitationsActions.DeclineInvitation, { cancelUncompleted: true })
  decline(
    ctx: StateContext<InvitationsStateModel>,
    { invitationId }: InvitationsActions.DeclineInvitation
  ): Observable<void | boolean> {
    ctx.patchState({ loading: true });

    return this.service.declineInvitation(invitationId).pipe(
      tap(() =>
        ctx.dispatch(new InvitationsActions.DeclineInvitationSuccess())
      ),
      catchError((e: HttpErrorResponse) =>
        ctx.dispatch(new InvitationsActions.DeclineInvitationFailed(e))
      )
    );
  }

  @Action(InvitationsActions.CancelInvitation, { cancelUncompleted: true })
  cancel(
    ctx: StateContext<InvitationsStateModel>,
    { invitationId }: InvitationsActions.CancelInvitation
  ): Observable<void | boolean> {
    ctx.patchState({ loading: true });

    return this.service.cancelInvitation(invitationId).pipe(
      tap(() => ctx.dispatch(new InvitationsActions.CancelInvitationSuccess())),
      catchError((e: HttpErrorResponse) =>
        ctx.dispatch(new InvitationsActions.CancelInvitationFailed(e))
      )
    );
  }

  @Action([
    InvitationsActions.GetAllInvitationsSuccess,
    InvitationsActions.GetAllInvitationsFailed,
    InvitationsActions.SendInvitationSuccess,
    InvitationsActions.SendInvitationFailed,
    InvitationsActions.AcceptInvitationSuccess,
    InvitationsActions.AcceptInvitationFailed,
    InvitationsActions.DeclineInvitationSuccess,
    InvitationsActions.DeclineInvitationFailed,
    InvitationsActions.CancelInvitationSuccess,
    InvitationsActions.CancelInvitationFailed,
  ])
  stopLoading(ctx: StateContext<InvitationsStateModel>): void {
    ctx.patchState({ loading: false });
  }
}
